/**
 * zt-story.js — behaviour for sections/zt-story.liquid
 * ------------------------------------------------------------------
 * 1. Chapter → stage state. One IntersectionObserver watches every
 *    [data-zt-chapter]; the one crossing the middle band of the viewport
 *    becomes `data-state` on <zt-story>. All visuals are CSS.
 * 2. Before/after slider. The native range input writes `--zt-split`
 *    (throttled to one write per frame). No pointer maths, no drag code.
 * 3. Live Web Vitals. PerformanceObserver reports LCP / CLS / INP of this
 *    very page; TTFB comes from Navigation Timing. Values are formatted and
 *    colour-coded against Google's "good" thresholds. If an API is missing
 *    the tile simply keeps its dash.
 *
 * Nothing here touches layout on scroll; the observer callback only sets an
 * attribute, and the browser handles the CSS transitions on the compositor.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class ZtStory extends HTMLElement {
  connectedCallback() {
    this.chapters = Array.from(this.querySelectorAll('[data-zt-chapter]'));
    this.#observeChapters();
    this.#initSlider();
    this.#initVitals();
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.po?.forEach((o) => o.disconnect());
  }

  /* ---- 1. state ---------------------------------------------------- */
  #observeChapters() {
    // A band in the middle of the viewport (40% tall) decides the active chapter;
    // whichever chapter intersects that band most recently wins.
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const next = entry.target.dataset.ztChapter;
          if (next && next !== this.dataset.state) this.dataset.state = next;
        }
      },
      { rootMargin: '-30% 0px -30% 0px', threshold: 0 }
    );
    this.chapters.forEach((c) => this.io.observe(c));
  }

  /* ---- 2. slider --------------------------------------------------- */
  #initSlider() {
    const input = this.querySelector('[data-zt-split]');
    if (!input) return;
    let pending = false;
    const apply = () => {
      pending = false;
      this.style.setProperty('--zt-split', input.value + '%');
    };
    input.addEventListener('input', () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(apply);
    }, { passive: true });
    // Gentle nudge the first time the diff chapter is reached so the visitor
    // notices the slider is live (skipped for reduced motion).
    if (!REDUCED.matches) {
      const once = new MutationObserver(() => {
        if (this.dataset.state !== 'diff') return;
        once.disconnect();
        let t = 0;
        const from = Number(input.value);
        const tick = () => {
          t += 1 / 60;
          const p = Math.min(1, t / 1.6);
          const ease = 0.5 - Math.cos(p * Math.PI * 2) / 2; // out and back
          input.value = String(Math.round(from + ease * 22));
          input.dispatchEvent(new Event('input'));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
      once.observe(this, { attributes: true, attributeFilter: ['data-state'] });
    }
  }

  /* ---- 3. vitals --------------------------------------------------- */
  #initVitals() {
    const root = this.querySelector('[data-zt-vitals]');
    if (!root || !('PerformanceObserver' in window)) return;
    const tile = (k) => root.querySelector(`[data-vital="${k}"]`);
    const set = (k, value, good) => {
      const el = tile(k);
      if (!el) return;
      el.textContent = value;
      el.classList.toggle('is-good', good);
    };
    this.po = [];
    const observe = (type, cb, extra = {}) => {
      try {
        const o = new PerformanceObserver((list) => cb(list.getEntries()));
        o.observe({ type, buffered: true, ...extra });
        this.po.push(o);
      } catch (_) { /* unsupported entry type — leave the dash */ }
    };

    // TTFB
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav) set('ttfb', Math.round(nav.responseStart) + ' ms', nav.responseStart < 800);

    // LCP — keep the latest candidate
    observe('largest-contentful-paint', (entries) => {
      const last = entries[entries.length - 1];
      if (last) set('lcp', (last.startTime / 1000).toFixed(2) + ' s', last.startTime < 2500);
    });

    // CLS — sum of shifts not caused by user input
    let cls = 0;
    set('cls', '0.000', true);
    observe('layout-shift', (entries) => {
      for (const e of entries) if (!e.hadRecentInput) cls += e.value;
      set('cls', cls.toFixed(3), cls < 0.1);
    });

    // INP — approximate with the worst interaction duration seen so far
    let worst = 0;
    observe('event', (entries) => {
      for (const e of entries) if (e.interactionId && e.duration > worst) worst = e.duration;
      if (worst) set('inp', Math.round(worst) + ' ms', worst < 200);
    }, { durationThreshold: 16 });
    if (!worst) set('inp', '–', true);
  }
}

if (!customElements.get('zt-story')) customElements.define('zt-story', ZtStory);
